/* eslint-disable @typescript-eslint/no-unsafe-return */
import { PageState } from 'models/PageState';
import { checkoutForm } from 'views/CheckoutPage/selector';
import { CheckoutDeliveryCarriageMethod } from 'models/CheckoutDeliveryCarriageMethod';
import { CheckoutDeliveryAdditionalService } from 'models/CheckoutDeliveryAdditionalService';
import { OptionConstraint } from 'models/OptionConstraint';
import { isCheckoutPage } from 'models/CheckoutPageModel';
import { DeliveryOptionItem } from 'models/DeliveryOptionItem';

export const getDeliveries = (state: PageState) =>
  isCheckoutPage(state.currentPage) ? state.currentPage.deliveries : [];

export const getUnfulfilledProducts = (state: PageState) =>
  isCheckoutPage(state.currentPage) ? state.currentPage.unfulfilledProducts : [];

export const showDeliveries = (state: PageState) =>
  isCheckoutPage(state.currentPage) && state.currentPage.showDeliveries;

export const isServicePoint = (option: DeliveryOptionItem): option is DeliveryOptionItem =>
  option.carriageMethodFe === CheckoutDeliveryCarriageMethod.OUTDELIVERYALTERNATIVE &&
  option.servicePointInformation !== undefined;

export const isHomeDelivery = (option?: DeliveryOptionItem): option is DeliveryOptionItem =>
  typeof option !== undefined &&
  (option as DeliveryOptionItem).carriageMethodFe !==
    CheckoutDeliveryCarriageMethod.OUTDELIVERYALTERNATIVE;

export const hasServicePointOptions = (options: DeliveryOptionItem[]) =>
  options !== undefined && options.length > 0 && options.some(isServicePoint);

export const isServicePointDeliveryGroup = (options: DeliveryOptionItem[]) =>
  options !== undefined && options.length > 0 && options.every(isServicePoint);

// The typeId is what identifies a deliveryOption, and it is a string that contains only numbers
// redux-form parses this to an array according to this issue
// https://github.com/erikras/redux-form/issues/2388
// therefor, we have to interpolate this number with a string
export const deliveryKey = (deliveryId: string) => `deliveries.delivery-${deliveryId}`;

export const deliverySelectedKey = (deliveryId: string) => `${deliveryKey(deliveryId)}.selected`;

export const deliveryAdditionalServicesKey = (deliveryId: string) =>
  `${deliveryKey(deliveryId)}.additionalServices`;

export const deliveryTimeSlotKey = (identifier: string) => `${deliveryKey(identifier)}.timeslot`;

export const getAdditionalServices = (state: PageState, identifier: string): string[] =>
  checkoutForm(state, deliveryAdditionalServicesKey(identifier)) || [];

export const getSelectedDelivery = (state: PageState, deliveryId: string): string | undefined =>
  checkoutForm(state, deliverySelectedKey(deliveryId));

export const getSelectedDeliverySlot = (state: PageState, deliveryId: string): string | undefined =>
  checkoutForm(state, deliveryTimeSlotKey(deliveryId));

export const selectShouldUseNewCart = (state: PageState) => state.appShell.shouldUseNewCart;

export const isSingleDelivery = (state: PageState) =>
  isCheckoutPage(state.currentPage) && state.currentPage.deliveries.length === 1;

export const getHomeDeliveryOption = (deliveryOptions: DeliveryOptionItem[]) =>
  deliveryOptions.find(isHomeDelivery);

export const getRequiredServices = (
  service: CheckoutDeliveryAdditionalService,
  constraints: Pick<OptionConstraint, 'additionalServices'>,
  additionalServices: CheckoutDeliveryAdditionalService[]
): CheckoutDeliveryAdditionalService[] =>
  constraints.additionalServices[service.id].require
    ? additionalServices.filter(({ type }) =>
        constraints.additionalServices[service.id].require.includes(type.valueOf())
      )
    : [];

export const getRequiredByServices = (
  service: CheckoutDeliveryAdditionalService,
  constraints: Pick<OptionConstraint, 'additionalServices'>
) =>
  Object.keys(constraints.additionalServices).filter(
    k =>
      k !== service.id &&
      constraints.additionalServices[k].require &&
      constraints.additionalServices[k].require.includes(service.type.valueOf())
  );
